import React, { MouseEventHandler, useState, useEffect } from 'react';
import { getBackendApiUrl } from "../../../config";
import Spinner from './spinner';
import { addAuthorizationHeader } from '../../../api/network';
import { DefaultCustomerData, DesignDoc } from '../../../types/api-types';

// Styles
import styles from './contactInformationModal.module.css';

// Defining the prop types
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdateContactSuccess: () => void;
  document: DesignDoc;
  defaultCustomerData: DefaultCustomerData;
}

const ContactInformationModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  onUpdateContactSuccess,
  document,
  defaultCustomerData
}) => {
  const [formData, setFormData] = useState<DefaultCustomerData>({
    email: '',
    fullName: '',
    phone: ''
  });
  const [processing, setProcessing] = useState<boolean>(false);

  useEffect(() => {
    if (document) {
      setFormData({
        email: document.notificationsEmail || '',
        fullName: document?.contactName || '',
        phone: document?.contactPhone || ''
      });
    }
  }, [document]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleUpdateContactData(e as any);
  };

  const handleInputChange = (field: keyof DefaultCustomerData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleUpdateContactData: MouseEventHandler<HTMLButtonElement> = async (e) => {
    e.preventDefault();
    if (!document?.id) return;

    setProcessing(true);

    try {
      const headers = addAuthorizationHeader({
        'Content-Type': 'application/json'
      });

      const body = {
        email: formData.email.trim(),
        fullName: formData.fullName.trim(),
        phone: formData.phone.trim()
      };

      const result = await fetch(`${getBackendApiUrl()}/design-contact-data/${document.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(body)
      });

      if (!result.ok) {
        throw new Error("Error updating contact information: " + await result.text());
      }

      onUpdateContactSuccess();
    } catch (err) {
      alert("Failed to update contact information: " + (err as Error).message);
    } finally {
      setProcessing(false);
    }
  };

  const isValidInput = formData.email.trim().length === 0 ||
    (formData.email.trim().length > 0 && formData.email.includes('@'));

  const isUsingDefaultEmail = !document?.notificationsEmail;
  const isUsingDefaultName = !document?.contactName;
  const isUsingDefaultPhone = !document?.contactPhone;

  function CloseIcon() {
    return (
      <button
        type="button"
        className="bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none"
        aria-label="Close modal"
      >
        <span className="sr-only">Close menu</span>
        <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    );
  }

  return isOpen ? (
    <div className={styles.modalOverlay}>
      <div className={`${styles.modalContainer} relative w-auto my-auto mx-auto max-w-3xl`}>
        <div className={`${styles.modalWrapper} border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none`}>
          <div className={styles.header}>
            <span>Update Contact Information</span>
            <div className={styles.closeIcon} onClick={onClose}>
              <CloseIcon />
            </div>
          </div>
          <div className={styles.modalContent}>
            <div className="mb-6 p-4 bg-[#FFF4E5] border border-orange-200 rounded-md">
              <p className="text-sm text-[#663c00]">
                <strong>Note:</strong> Leave any field blank to use the default customer information
              </p>
            </div>
            <div className='flex flex-col w-4/5'>
              <form
                onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Notifications Email:
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange('email')}
                    disabled={processing}
                    placeholder="Enter notifications email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none"
                  />
                  {defaultCustomerData.email && isUsingDefaultEmail && (
                    <div className="mt-2 text-sm">
                      <div>
                        Default: <span className='text-[#F36E29] font-medium'>{defaultCustomerData.email}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mb-4">
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Name:
                  </label>
                  <input
                    id="fullName"
                    type="text"
                    value={formData.fullName}
                    onChange={handleInputChange('fullName')}
                    disabled={processing}
                    placeholder="Enter contact name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none"
                  />
                  {defaultCustomerData.fullName && isUsingDefaultName && (
                    <div className="mt-2 text-sm">
                      <div>
                        Default: <span className='text-[#F36E29] font-medium'>{defaultCustomerData.fullName}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mb-4">
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Phone:
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange('phone')}
                    disabled={processing}
                    placeholder="Enter contact phone"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none"
                  />
                  {defaultCustomerData.phone && isUsingDefaultPhone && (
                    <div className="mt-2 text-sm">
                      <div>
                        Default: <span className='text-[#F36E29] font-medium'>{defaultCustomerData.phone}</span>
                      </div>
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
          <div className={`${styles.footerButtons} p-6 border-t border-solid border-blueGray-200 rounded-b`}>
            <button
              className={[styles.button, styles.cancelBtn].join(' ')}
              onClick={onClose}
              disabled={processing}
            >
              Close
            </button>
            <button
              className={styles.acceptBtn}
              onClick={handleUpdateContactData}
              disabled={processing || !isValidInput}
            >
              {processing ? (
                <Spinner spinning={processing} color="#d1d5db" size={{ width: "22px", height: "22px" }} />
              ) : (
                "Save"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : null;
};

export default ContactInformationModal;